import WithAdditionalValidationState from 'components/WithAdditionalValidation/WithAdditionalValidationState';
import FormImageUploader from 'components/FormImageUploader/FormImageUploader';
import { FormImageUploaderNew } from 'components/FormImageUploaderNew/FormImageUploaderNew';
import { EditDishContext, IEditDishContext } from 'modules/Menu/Dishes/EditDish/editDishState/editDishContext';
import React, { useContext, useMemo } from 'react';
import { InputFields } from './InputFields/InputFields';
import classNames from 'classnames';
import style from './EditForm.module.scss';

const descriptionOfAspectRatio = {
	horizontal: {
		ratio: '95:46',
		optimalResolutions: {
			a: '380*184px',
			b: '760*368px',
		},
	},
	vertical: {
		ratio: '44:19',
		optimalResolutions: {
			a: '528*228px',
			b: '1056*456px',
		},
	},
};

const detailDescriptionOfAspectRatio = {
	horizontal: {
		ratio: '95:46',
		optimalResolutions: {
			a: '380*184px',
			b: '760*368px',
		},
	},
	vertical: {
		ratio: '44:19',
		optimalResolutions: {
			a: '528*228px',
			b: '1056*456px',
		},
	},
};

const smallDescriptionOfAspectRatio = {
	horizontal: {
		ratio: '2:1',
		optimalResolutions: {
			a: '480*240px',
			b: '960*480px',
		},
	},
	vertical: {
		ratio: '1:2',
		optimalResolutions: {
			a: '160*320px',
			b: '320*640px',
		},
	},
};

interface IEditForm {
	isVisible: boolean;
}

export const EditForm: React.FC<IEditForm> = ({ isVisible }) => {
	const {
		setFileId,
		deletePhoto,
		fileId,
		filePath,
		preSignedUrl,
		setSmallFileId,
		deleteSmallPhoto,
		smallFileId,
		smallFilePath,
		smallPreSignedUrl,
		setVideoFileId,
		deleteVideo,
		videoFileId,
		videoPath,
		videoPreSignedUrl,
		isVideoActive,
		setIsVideoActive,
		dishId,
	} = useContext<IEditDishContext>(EditDishContext);

	const containerClasses = classNames(style.container, { [style.visible]: isVisible });

	const hasDetailContent = isVideoActive ? Boolean(videoFileId || videoPath) : Boolean(smallFileId || smallFilePath);

	const loadFromFilePathOptions = useMemo(
		() => ({
			filePath,
			preSignedUrl,
			entityID: dishId,
		}),
		[filePath, preSignedUrl, dishId],
	);

	const loadFromSmallFilePathOptions = useMemo(
		() => ({
			filePath: smallFilePath,
			preSignedUrl: smallPreSignedUrl,
			entityID: dishId,
		}),
		[smallFilePath, smallPreSignedUrl, dishId],
	);

	const loadFromVideoFilePathOptions = useMemo(
		() => ({
			filePath: videoPath,
			preSignedUrl: videoPreSignedUrl,
			entityID: dishId,
		}),
		[videoPath, videoPreSignedUrl, dishId],
	);

	// Swapping `filePath` and `smallFilePath` is not an error; there was an urgent business requirement to swap them.

	return (
		<div className={containerClasses} data-test="EditForm">
			<WithAdditionalValidationState>
				{process.env.HAS_DISH_GIF_UPLOAD ? (
					<div className={style.imageUploaders}>
						<FormImageUploaderNew
							maxFileSizeMB={{ img: 1 }}
							fileId={fileId}
							loadFromFilePathOptions={loadFromFilePathOptions}
							deletePhotoHandler={deletePhoto}
							setFileId={setFileId}
							horizontalAspectRatio={480 / 240}
							verticalAspectRatio={160 / 320}
							descriptionOfAspectRatio={smallDescriptionOfAspectRatio}
							modalTitleTranslationKey="dishCardContentTitle"
							modalSubtitleTranslationKey="dishCardContentModalSubtitle"
							cardTitleTranslationKey="dishCardContentTitle"
							cardSubtitleTranslationKey="dishCardContentSubtitle"
							withValidation={true}
							isRequired={hasDetailContent}
							validationErrorTranslationKey="dishCardContentNecessary"
							validationFieldName="dishCardContent"
						/>
						<div className="w-full border-0 border-t border-solid border-grey-1400" />
						<FormImageUploaderNew
							maxFileSizeMB={{ img: 15, gif: 15, video: 15 }}
							fileId={smallFileId}
							loadFromFilePathOptions={loadFromSmallFilePathOptions}
							deletePhotoHandler={deleteSmallPhoto}
							setFileId={setSmallFileId}
							horizontalAspectRatio={208 / 128}
							verticalAspectRatio={380 / 184}
							descriptionOfAspectRatio={detailDescriptionOfAspectRatio}
							modalTitleTranslationKey="dishDetailPageContentTitle"
							modalSubtitleTranslationKey="dishDetailPageContentSubtitle"
							cardTitleTranslationKey="dishDetailPageContentTitle"
							cardSubtitleTranslationKey="dishDetailPageContentCardSubtitle"
							setVideoFileId={setVideoFileId}
							deleteVideoHandler={deleteVideo}
							loadFromVideoFilePathOptions={loadFromVideoFilePathOptions}
							hasVideo={isVideoActive}
							setIsVideoActive={setIsVideoActive}
						/>
					</div>
				) : (
					<FormImageUploader
						containerClass={style.imageUploaders}
						maxFileSizeMB={1}
						fileId={fileId}
						loadFromFilePathOptions={loadFromFilePathOptions}
						deletePhotoHandler={deletePhoto}
						setFileId={setFileId}
						horizontalAspectRatio={380 / 184}
						verticalAspectRatio={528 / 228}
						descriptionOfAspectRatio={descriptionOfAspectRatio}
					/>
				)}
				<InputFields />
			</WithAdditionalValidationState>
		</div>
	);
};
